import { PortalModule } from '@angular/cdk/portal';
import { NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { Subject, forkJoin } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { DialogService, FeedbackService, FlyoutService, HttpErrorService, NavigationService } from '@app/core/services';
import { SiteCode, UserRole } from '@app/models/fund-management';
import { BaseFlyoutComponent } from '@app/shared/components';
import { MessageListComponent, MessageListModel } from '@app/shared/components/message-list/message-list.component';
import { NgForTrackByIdDirective } from '@app/shared/directives/ng-for-track-by-id.directive';
import { RequiredNoWhitespaceDirective } from '@app/shared/directives/validators/required-no-whitespace.directive';
import { AccountStructureService } from '@app/shared/services';
import { AccountCopyService } from './services/account-copy.service';

@Component({
	selector: 'account-copy',
	templateUrl: './account-copy.component.html',
	styleUrls: ['./account-copy.component.less'],
	standalone: true,
	imports: [
		PortalModule,
		MatIconModule,
		MatTabsModule,
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		NgxMaskDirective,
		NgIf,
		RequiredNoWhitespaceDirective,
		MatSelectModule,
		MatOptionModule,
		NgFor,
		NgForTrackByIdDirective,
		MatTooltipModule,
		MatSlideToggleModule,
		MatButtonModule,
		MessageListComponent,
	],
})
export class AccountCopyComponent extends BaseFlyoutComponent implements OnInit, OnDestroy {
	@ViewChild('copyToSitesForm') copyToSitesForm: NgForm;
	@ViewChild('copyAccountForm') copyAccountForm: NgForm;

	@Input()
	baseAccountId: number;

	@Input() set parentAccountNumberValue(value: number) {
		this.parentAccountNumber = value;
		this.baseParentAccountNumber = value;
	}

	@Input() set accountNumberValue(value: number) {
		this.accountNumber = value;
		this.baseAccountNumber = value;
	}

	@Input() set descriptionValue(value: string) {
		this.description = value;
		this.baseDescription = value;
	}
	@Output()
	accountsCreated = new EventEmitter<void>();

	parentAccountNumber: number;
	accountNumber: number;

	header = 'Copy Account';

	allSitesSelected = false;
	siteCodes: SiteCode[] = [];
	enabledSiteCodes: SiteCode[] = [];
	selectedSiteCodeIds: number[] = [];
	allSiteCodesId = -1;

	baseAccountIsParent = false;

	allUserRolesSelected = false;
	userRoles: UserRole[] = [];
	selectedUserRoleIds: string[] = [];
	allUserRolesId = '-1';
	accountPattern = { 0: { pattern: /^[a-zA-Z0-9?]+$/ } };
	unifiedAccountMask = '0.0000.000.000.000.000.00';
	description: string;
	selectedTabIndex = 0;

	copyAccountMessageList = [
		{
			MessageType: 'info',
			Message:
				`COPY ACCOUNT allows you to clone the current selected account.  You provide the new account number ` +
				`with other information and submit to create the new account.  Selected Roles are required ` +
				`to ensure the new account is created properly.
`,
		} as MessageListModel,
	];

	copyToSitesMessageList = [
		{
			MessageType: 'info',
			Message:
				`COPY TO SITES allows you to create a cloned account or accounts based off the sites you select. ` +
				`The system will take the current selected account and use it to create one or many accounts with the site designation ` +
				`included in the new account numbers.  Note!  Account structure must have a designated "Site Element" for this process ` +
				`to be successful.
`,
		} as MessageListModel,
	];

	protected readonly destroy$ = new Subject();
	private baseAccountNumber: number;
	protected baseParentAccountNumber: number;
	private baseDescription: string;

	constructor(
		protected readonly navigationService: NavigationService,
		protected readonly dialog: MatDialog,
		protected readonly dialogService: DialogService,
		protected readonly feedbackService: FeedbackService,
		protected readonly router: Router,
		protected readonly flyoutService: FlyoutService,
		protected readonly accountCopyService: AccountCopyService,
		protected readonly errorService: HttpErrorService,
		protected readonly accountStructureService: AccountStructureService,
	) {
		super(flyoutService, dialogService);
	}

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => !this.copyToSitesForm?.dirty && !this.copyAccountForm?.dirty;

	ngOnInit(): void {
		forkJoin([
			this.accountCopyService.getSitesForCopy(),
			this.accountCopyService.getRolesForCopy().pipe(map((item) => item.map((role) => new UserRole(role.Id, role.Name, role.Description)))),
			this.accountStructureService.getStructure(),
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([siteCodes, userRoles, structure]) => {
				this.siteCodes = siteCodes;
				this.enabledSiteCodes = siteCodes.filter((item) => !item.Disabled);
				this.siteCodes = siteCodes;
				this.userRoles = userRoles;
				this.unifiedAccountMask = structure.accountStructure.UnifiedAccountMask;
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	copyToSites(): void {
		this.errorService.suppressDefaultError();
		this.accountCopyService
			.copyToSites({
				BaseAccountId: this.baseAccountId,
				RoleIds: this.selectedUserRoleIds.filter((item) => item !== this.allUserRolesId),
				SiteIds: this.selectedSiteCodeIds.filter((item) => item !== this.allSiteCodesId),
				UseBaseAccountAsParent: this.baseAccountIsParent,
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.resetFormOnClose();
					this.closeFlyout();
					this.accountsCreated.emit();
					this.feedbackService.customSuccess('COPY TO SITES process was successful.');
				},
				error: (errorResponse: HttpErrorResponse) => {
					if (errorResponse.status < 400 || errorResponse.status > 499) {
						return;
					}
					const errorMessage: string = errorResponse.error?.ExceptionMessage || errorResponse.error?.Message || errorResponse.statusText;
					if (!errorMessage) {
						return;
					}

					const errorsMessages = errorMessage.split(';');
					if (errorsMessages.length > 1) {
						this.feedbackService.error('Errors occurred while copying');
					}

					errorsMessages.forEach((msg) => this.feedbackService.error(msg));
				},
			});
	}

	copyAccount(): void {
		this.accountCopyService
			.copyAccount({
				ParentAccountNumber: this.parentAccountNumber,
				Description: this.description,
				AccountNumber: this.accountNumber,
				RoleIds: this.selectedUserRoleIds.filter((item) => item !== this.allUserRolesId),
				OriginalAccountNumber: this.baseAccountNumber,
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.resetFormOnClose();
				this.closeFlyout();
				this.accountsCreated.emit();
				this.feedbackService.customSuccess('COPY ACCOUNT process was successful.');
			});
	}

	toggleAllSitesSelections(): void {
		this.allSitesSelected = !this.allSitesSelected;

		if (this.allSitesSelected) {
			this.selectedSiteCodeIds = [this.allSiteCodesId, ...this.enabledSiteCodes.map((item) => item.Id)];
		} else {
			this.selectedSiteCodeIds = [];
		}
	}

	checkSiteOption(): void {
		if (this.allSitesSelected) {
			this.selectedSiteCodeIds = this.selectedSiteCodeIds.filter((item) => item !== this.allSiteCodesId);
			this.allSitesSelected = false;
		} else if (this.selectedSiteCodeIds.length >= this.enabledSiteCodes.length) {
			this.toggleAllSitesSelections();
		}
	}

	getSitesMultiSelectDisplayText(): string {
		if (this.enabledSiteCodes.length < 1 || !this.selectedSiteCodeIds?.length) {
			return '';
		}

		if (this.allSitesSelected) {
			return 'All';
		}
		const firstSelected = this.siteCodes.find((item) => item.Id === this.selectedSiteCodeIds[0]);
		const firstValue = `${firstSelected.SiteCode} - ${firstSelected.SiteName}`;
		const numberOthersChecked = this.selectedSiteCodeIds.length - 1;
		if (numberOthersChecked > 0) {
			return `${firstValue} <em>(+${numberOthersChecked} other${numberOthersChecked > 1 ? 's' : ''})</em>`;
		}

		return firstValue;
	}

	toggleAllRolesSelections(): void {
		this.allUserRolesSelected = !this.allUserRolesSelected;

		if (this.allUserRolesSelected) {
			this.selectedUserRoleIds = [this.allUserRolesId, ...this.userRoles.map((item) => item.Id)];
		} else {
			this.selectedUserRoleIds = [];
		}
	}

	checkRoleOption(): void {
		if (this.allUserRolesSelected) {
			this.selectedUserRoleIds = this.selectedUserRoleIds.filter((item) => item !== this.allUserRolesId);
			this.allUserRolesSelected = false;
		} else if (this.selectedUserRoleIds.length >= this.userRoles.length) {
			this.toggleAllRolesSelections();
		}
	}

	getRolesMultiSelectDisplayText(): string {
		if (!this.selectedUserRoleIds?.length) {
			return '';
		}

		if (this.allUserRolesSelected) {
			return 'All';
		}
		const firstSelected = this.userRoles.find((item) => item.Id === this.selectedUserRoleIds[0]);
		const firstValue = `${firstSelected.NameToDisplay}`;
		const numberOthersChecked = this.selectedUserRoleIds.length - 1;
		if (numberOthersChecked > 0) {
			return `${firstValue} <em>(+${numberOthersChecked} other${numberOthersChecked > 1 ? 's' : ''})</em>`;
		}

		return firstValue;
	}

	toggleBaseAccount(): void {
		this.baseAccountIsParent = !this.baseAccountIsParent;
	}

	closeFlyout(): void {
		this.close()
			.pipe(take(1))
			.subscribe((closed) => {
				if (closed) {
					this.clearData();
				}
			});
	}

	handleSubmitCopyToSites(): void {
		this.copyToSitesForm.ngSubmit.emit();
	}

	handleSubmitCopyAccountForm(): void {
		this.copyAccountForm.ngSubmit.emit();
	}

	sameAccountNumber(): boolean {
		if (this.accountNumber === this.baseAccountNumber) {
			if (this.copyAccountForm) {
				this.copyAccountForm.form?.controls?.accountNumber?.markAsTouched();
				this.copyAccountForm.form?.controls?.accountNumber?.setErrors({ sameNumber: true });
			}
			return true;
		} else {
			return false;
		}
	}
	private clearData(): void {
		this.allUserRolesSelected = false;
		this.selectedUserRoleIds = [];
		this.allSitesSelected = false;
		this.selectedSiteCodeIds = [];
		this.baseAccountIsParent = false;
		this.description = this.baseDescription;
		this.accountNumber = this.baseAccountNumber;
		this.parentAccountNumber = this.baseParentAccountNumber;
	}

	private resetFormOnClose(): void {
		this.copyAccountForm?.form.reset();
		this.copyAccountForm?.form.markAsPristine();
		this.copyToSitesForm?.form.reset();
		this.copyToSitesForm?.form.markAsPristine();
	}
}
